import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
} from '@goodday-clone/ui'
import {
  Users,
  Building,
  Mail,
  Phone,
  Layers,
  DollarSign,
  Flame,
  User,
  Clock,
  FileText,
  Plus,
} from 'lucide-react'
import { LeadDetailData } from './lead-detail-modal'
import { useAuth } from '@/context/AuthContext'
import { toast } from 'react-hot-toast'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { useLeadSources, useLeadStatuses } from '@/shared/hooks/useCrmVocabulary'
import { ServicesPicker } from './ServicesPicker'
import { useProductServices, ProductService } from '@/shared/hooks/useProductServices'

interface AssignableUser {
  id: string
  name: string
  department?: string
  departmentRole?: string
}

interface AddLeadModalProps {
  isOpen: boolean
  onClose: () => void
  onLeadCreated: (newLead: LeadDetailData) => void
  initialSource?: string
  initialStatus?: string
  initialAssignee?: string
}

const INTEREST_OPTIONS = ['Warm', 'Hot', 'Cold']

export function AddLeadModal({
  isOpen,
  onClose,
  onLeadCreated,
  initialSource = 'Trade Show',
  initialStatus = 'New',
  initialAssignee,
}: AddLeadModalProps) {
  const { user: currentUser } = useAuth()
  const { data: leadSources = [] } = useLeadSources()
  const { data: leadStatuses = [] } = useLeadStatuses()
  const { data: masterServices = [] } = useProductServices({ isActive: true })
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([])

  useEffect(() => {
    if (!isOpen) return
    api.get<{ data: AssignableUser[] }>('/users/assignable?for=leads')
      .then((res) => setAssignableUsers(res.data || []))
      .catch(() => setAssignableUsers([]))
  }, [isOpen])

  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [role, setRole] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [source, setSource] = useState(initialSource)
  const [status, setStatus] = useState(initialStatus)
  const [interest, setInterest] = useState('Warm')
  const [assignedToUserId, setAssignedToUserId] = useState(initialAssignee || currentUser?.id || '')
  const [value, setValue] = useState('')
  const [notes, setNotes] = useState('')
  const [services, setServices] = useState<string[]>([])
  const [rating, setRating] = useState<number | null>(null)
  const [hoverRating, setHoverRating] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Master-data service lookup (name → defaultRate)
  const masterDataByName = useMemo(() => {
    const map: Record<string, ProductService> = {}
    for (const ps of masterServices) {
      if (ps.name) map[ps.name.toLowerCase()] = ps
    }
    return map
  }, [masterServices])

  // Auto-calculated total from selected master data services
  const totalFromServices = useMemo(() => {
    if (services.length === 0) return 0
    return services.reduce((sum, svc) => {
      const md = masterDataByName[svc.toLowerCase()]
      return sum + (md?.defaultRate || 0)
    }, 0)
  }, [services, masterDataByName])

  // Auto-sync the value field with the calculated total from services when services change
  // (but only if the user hasn't manually overridden the value)
  const valueInputRef = useRef<HTMLInputElement>(null)
  const userModifiedValue = useRef(false)

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    userModifiedValue.current = true
  }

  // Auto-populate value from master data services when they change (respects manual override)
  useEffect(() => {
    if (services.length > 0 && totalFromServices > 0 && !userModifiedValue.current) {
      setValue(String(totalFromServices))
    }
  }, [totalFromServices, services.length])

  const resetValueOverride = () => {
    userModifiedValue.current = false
    if (services.length > 0 && totalFromServices > 0) {
      setValue(String(totalFromServices))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Lead name is required')
      return
    }
    if (!email.trim()) {
      setError('Email address is required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address')
      return
    }
    if (!phone.trim()) {
      setError('Phone number is required')
      return
    }

    setIsSubmitting(true)
    setError(null)

    // Use the total from services if available, otherwise the manual input
    const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0
    const finalValue = services.length > 0 && totalFromServices > 0 ? totalFromServices : numValue
    const formattedValue = `$${finalValue.toLocaleString()}`
    const assigneeName = assignableUsers.find((u) => u.id === assignedToUserId)?.name || currentUser?.name || ''

    const newLeadObj: LeadDetailData = {
      id: `lead-${Date.now()}`,
      name: name.trim(),
      company: company.trim() || undefined,
      role: role.trim() || undefined,
      contactPerson: contactPerson.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      source,
      status,
      interest,
      rating: rating ?? undefined,
      assignee: assigneeName,
      value: formattedValue,
      notes: notes.trim() || undefined,
      services,
      number: String(Math.floor(Math.random() * 90) + 10),
      createdAt: new Date().toISOString(),
    }

    try {
      const created = await api.post<any>('/leads', {
        name: newLeadObj.name,
        company: newLeadObj.company,
        role: newLeadObj.role,
        contactPerson: newLeadObj.contactPerson,
        email: newLeadObj.email,
        phone: newLeadObj.phone,
        source: newLeadObj.source,
        status: newLeadObj.status,
        interest: newLeadObj.interest,
        rating: rating ?? undefined,
        assignedToUserId: assignedToUserId || undefined,
        value: finalValue,
        notes: newLeadObj.notes,
        services,
      }, { skipErrorToast: true })

      const savedLead = created?.data || created
      if (savedLead?._id || savedLead?.id) {
        newLeadObj.id = savedLead._id || savedLead.id
      }

      onLeadCreated(newLeadObj)
      onClose()

      setName('')
      setCompany('')
      setContactPerson('')
      setRole('')
      setEmail('')
      setPhone('')
      setValue('')
      setNotes('')
      setServices([])
      setRating(null)
      userModifiedValue.current = false
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'create leads'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl bg-card border border-border p-6 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-border/60">
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-foreground">
            <span className="p-2 rounded-xl bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </span>
            Add New Sales Lead
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Fill in the details below to add a new prospect to your sales pipeline
          </p>
        </DialogHeader>

        {error && (
          <div className="p-3 text-xs bg-destructive/10 text-destructive border border-destructive/20 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Row 1: Name & Company */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Lead Name / Opportunity *</Label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. RBM or Sarah Jenkins"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Company / Account</Label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. TitanEstate"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Row 2: Contact Person & Role */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Contact Person</Label>
              <Input
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                placeholder="e.g. Robert Singh"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Job Title / Role</Label>
              <Input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Procurement Lead"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Row 3: Email & Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Email Address *</Label>
              <Input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. contact@example.com"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Phone Number *</Label>
              <Input
                required
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 8975929541"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Row 4: Source, Status, Interest */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Lead Source</Label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {leadSources.map((src) => (
                  <option key={src.id} value={src.name}>
                    {src.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Status</Label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {leadStatuses.map((st) => (
                  <option key={st.id} value={st.name}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Interest Level</Label>
              <select
                value={interest}
                onChange={(e) => setInterest(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {INTEREST_OPTIONS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 5: Assignee, Total */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Assigned To</Label>
              <select
                value={assignedToUserId}
                onChange={(e) => setAssignedToUserId(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {assignableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.departmentRole ? ` — ${u.departmentRole}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Total (₹)</Label>
              <Input
                ref={valueInputRef}
                type="number"
                value={value}
                onChange={handleValueChange}
                onFocus={() => userModifiedValue.current = true}
                placeholder={totalFromServices > 0 ? `Auto: ${totalFromServices}` : "28000"}
                className="h-9 text-xs"
              />
              {totalFromServices > 0 && (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                  Auto-calculated from {services.length} selected service{services.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Row 5: Lead Rating */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Lead Rating</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(rating === star ? null : star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(null)}
                  className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
                  title={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <span
                    className={`text-xl leading-none ${
                      (hoverRating ?? rating ?? 0) >= star
                        ? 'text-amber-400'
                        : 'text-muted-foreground/30'
                    }`}
                  >
                    ★
                  </span>
                </button>
              ))}
              {rating ? (
                <span className="ml-2 text-xs text-muted-foreground">{rating} / 5</span>
              ) : (
                <span className="ml-2 text-xs text-muted-foreground/50">Not rated</span>
              )}
            </div>
          </div>

          {/* Row 6: Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Notes / Description</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter any initial discussion notes, requirements, or next steps..."
              rows={3}
              className="text-xs"
            />
          </div>

          {/* Row 7: Services Section */}
          <div className="pt-2 border-t border-border/60">
            <ServicesPicker
              selectedServices={services}
              onChange={setServices}
              label="Services"
              description="Select services required for this lead. Total is auto-calculated from master data rates."
              showRates={true}
              masterData={masterServices}
            />
          </div>

          <DialogFooter className="pt-3 border-t border-border/60">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="text-xs gap-1.5 shadow-sm"
            >
              <Plus className="h-3.5 w-3.5" />
              {isSubmitting ? 'Saving to Database...' : 'Add Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
